<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  knowledgebaseId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['close', 'editSitemap']);

const store = useStore();
const { t } = useI18n();

const sitemaps = computed(() => store.getters['sitemaps/getSites']);
const uiFlags = computed(() => store.getters['sitemaps/getUIFlags']);

onMounted(() => {
  store.dispatch('sitemaps/get', props.knowledgebaseId);
});

async function handleResync(site) {
  await store.dispatch('sitemaps/reindex', {
    knowledgebaseId: props.knowledgebaseId,
    siteId: site.id,
  });
  store.dispatch('sitemaps/get', props.knowledgebaseId);
}

const deletingId = ref(null);
async function handleDelete(site) {
  if (deletingId.value) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('LIBRARIES.SITEMAPS_MODAL.DELETE_CONFIRM'))) return;
  deletingId.value = site.id;
  try {
    await store.dispatch('sitemaps/delete', {
      knowledgebaseId: props.knowledgebaseId,
      siteId: site.id,
    });
  } finally {
    deletingId.value = null;
  }
}

const copiedId = ref(null);
async function handleCopyUrl(site) {
  await copyTextToClipboard(site.url);
  copiedId.value = site.id;
  setTimeout(() => {
    copiedId.value = null;
  }, 2000);
}
</script>

<template>
  <div class="h-auto overflow-auto flex flex-col">
    <woot-modal-header :header-title="t('LIBRARIES.SITEMAPS_MODAL.TITLE')" />

    <div class="flex flex-col w-full px-8 pb-8">
      <div v-if="uiFlags.isFetching" class="flex justify-center py-8">
        <Spinner :size="32" />
      </div>

      <div
        v-else-if="!sitemaps || sitemaps.length === 0"
        class="text-neutral-500 py-4 text-center"
      >
        {{ t('LIBRARIES.SITEMAPS_MODAL.NO_SITEMAPS') }}
      </div>

      <table v-else class="w-full woot-table">
        <thead class="border-b border-neutral-100">
          <tr>
            <th class="text-left">{{ t('LIBRARIES.SITEMAPS_MODAL.URL') }}</th>
            <th class="text-left w-24">
              {{ t('LIBRARIES.SITEMAPS_MODAL.STATUS') }}
            </th>
            <th class="text-left">
              {{ t('LIBRARIES.SITEMAPS_MODAL.REGEX') }}
            </th>
            <th class="text-left w-28">
              {{ t('LIBRARIES.SITEMAPS_MODAL.AUTOUPDATE') }}
            </th>
            <th class="w-32 text-right" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="site in sitemaps"
            :key="site.id"
            class="border-b border-neutral-100"
          >
            <td class="max-w-[14rem]">
              <div class="flex items-center gap-1">
                <span
                  v-tooltip.top="site.url"
                  class="font-mono text-sm truncate text-neutral-800"
                >
                  {{ site.url }}
                </span>
                <NextButton
                  v-tooltip.top="
                    copiedId === site.id
                      ? t('CONTACT_PANEL.COPY_SUCCESSFUL')
                      : t('LIBRARIES.SITEMAPS_MODAL.COPY_URL')
                  "
                  :icon="
                    copiedId === site.id
                      ? 'i-lucide-check'
                      : 'i-lucide-clipboard-copy'
                  "
                  xs
                  slate
                  @click="handleCopyUrl(site)"
                />
              </div>
            </td>
            <td>
              <span
                class="px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-700"
              >
                {{ site.status }}
              </span>
            </td>
            <td
              class="text-sm text-neutral-500 max-w-[10rem] truncate"
              :title="site.regex || ''"
            >
              {{ site.regex || '—' }}
            </td>
            <td class="text-sm text-neutral-500">
              {{
                site.autoupdate
                  ? t('LIBRARIES.SITEMAPS_MODAL.ON')
                  : t('LIBRARIES.SITEMAPS_MODAL.OFF')
              }}
            </td>
            <td class="text-right whitespace-nowrap">
              <div class="flex items-center justify-end gap-1">
                <NextButton
                  v-tooltip.top-end="t('LIBRARIES.SITEMAPS_MODAL.EDIT')"
                  icon="i-lucide-pencil"
                  sm
                  slate
                  @click="emit('editSitemap', site)"
                />
                <NextButton
                  v-tooltip.top-end="t('LIBRARIES.SITEMAPS_MODAL.RESYNC')"
                  icon="i-lucide-refresh-ccw-dot"
                  sm
                  slate
                  :is-loading="uiFlags.isReindexing"
                  @click="handleResync(site)"
                />
                <NextButton
                  v-tooltip.top-end="t('LIBRARIES.SITEMAPS_MODAL.DELETE')"
                  icon="i-lucide-trash-2"
                  sm
                  ruby
                  :is-loading="deletingId === site.id"
                  :disabled="deletingId !== null"
                  @click="handleDelete(site)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="flex justify-end mt-6">
        <NextButton
          :label="t('LIBRARIES.SITEMAPS_MODAL.CLOSE')"
          sm
          slate
          @click="emit('close')"
        />
      </div>
    </div>
  </div>
</template>
